import { useEffect, useRef, useState } from 'react';
import { WinCheckbox } from './WinComponents';
import DateInput from './DateInput';
import MoneyInput from './MoneyInput';
import { validateCourNo, formatCourNo, cleanCourNo, courNoMessage, maskCourNo } from '../lib/courNoValidation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Onglet Service — réplique compacte du `TabService` legacy (cf. MDIParent2.Designer.vb).
 *
 * Cascade dropdowns (équivalent legacy `ExploseNature` — CodeDaj.vb:4577) :
 *   Catégorie  → Nature  → Services
 *
 * Mapping legacy strict (cf. CodeDaj.vb:430-470 RempFin / 2020-2070 ToDaj) :
 *   adda501 → cboNatCateg     adda502 → cboServNat       adda512 → txtArticle
 *   adda500 → cboNature       adda510 → txtCodeNature    adda513 → txtNatAcces
 *   adda520 → txtFondDroit    adda540 → valEnDemand()    adda545 → chkSommaire
 *   adda544 → txtNoCause      adda560 → txtContrMax      adda570 → txtFraisAdmin
 *   coureelpre → txtCoutReel  versement → txtMontVerse   adda714 → txtDateSignReq
 *   memedaj + referdaj → chkMemAffair + txtMemeAffair
 *   GCRRecouv → chkGcrRecouv  GCRRecouvDate / Montant
 */
/**
 * Sous-composant : ligne "Même affaire que : XXXXXXXX" avec validation live.
 * Legacy : cf. CodeDaj.vb:2051-2056 (save) + CodeInfoFin.vb:507 (bypass ContMax).
 *
 * Comportement UX :
 *   - Case cochée → input activé (maxLength=8, MAJUSCULES)
 *   - Sur `onBlur` (ou 8 chars saisis) : appel GET /api/daj/exists pour vérifier
 *   - Feedback inline : ✓ vert avec le nom du requérant, ✗ rouge sinon
 *   - Bureau (2 chars) auto-rempli quand le dossier est trouvé
 */
/**
 * Sous-composant : ligne "Même affaire que : XXXXXXXX" avec validation live.
 * Legacy : cf. CodeDaj.vb:2594-2620 (ExisteNoref) + MDIParent2.vb:4283 (LostFocus).
 *
 * Comportement UX (parité legacy) :
 *   - Case cochée → input activé (maxLength=8, MAJUSCULES)
 *   - Sur `onBlur` (8 chars saisis) : appel GET /api/daj/exists
 *   - Match ✓ → auto-report `contribution_max` du dossier référencé
 *     dans `decision.contribution_max` (parité `txtContrMax.Text = adda560`,
 *     CodeDaj.vb:2603).
 *   - Match ✗ → warning informatif non-bloquant
 *   - Auto-référence (current_noref == ref) → feedback rouge dédié
 */
const MemeAffaireRow = ({ service, updateService, setFormData, noclient, region, bureau, currentNoref, handleUncheckMemeAffaire }) => {
  const [checkState, setCheckState] = useState({ status: 'idle', info: null });
  const meme = !!service.meme_affaire;
  const ref = (service.meme_affaire_ref || '').trim().toUpperCase();

  const validateRef = async (val) => {
    const v = (val || '').trim().toUpperCase();
    if (!v) { setCheckState({ status: 'idle', info: null }); return; }
    // Le legacy exige exactement 8 chars (MDIParent2.vb:4286, 4301, 4319).
    // Feedback court en dessous de 8 chars.
    if (v.length < 8) { setCheckState({ status: 'short', info: null }); return; }
    setCheckState({ status: 'checking', info: null });
    try {
      // Legacy ExisteNoref (CodeDaj.vb:2594) : cible MÊME client + BUREAU courant
      // (sauf région 18) + rejette auto-référence via current_noref.
      const res = await axios.get(`${API}/daj/exists`, {
        params: {
          noref: v,
          ...(noclient ? { noclient } : {}),
          ...(region ? { region } : {}),
          ...(bureau ? { bureau } : {}),
          ...(currentNoref ? { current_noref: currentNoref } : {}),
        },
      });
      if (res.data && res.data.exists) {
        setCheckState({ status: 'ok', info: res.data });
        // Legacy CodeDaj.vb:2603 : `MDIParent2.txtContrMax.Text = adda560`
        // Auto-report de la contribution max du dossier référencé.
        // On met à jour à la fois `decision.*` et `service.*` car le
        // template ServiceTab affiche `service.contribution_max ?? decision.*`
        // et ??  ne bascule pas sur `0` — il faut donc écrire dans les deux.
        const ref_contrib = Number(res.data.contribution_max) || 0;
        if (res.data.contribution_max != null && setFormData) {
          // Frais admin legacy standard : 50$ quand contribution > 0, 0 sinon.
          const frais = ref_contrib > 0 ? 50 : 0;
          setFormData(prev => ({
            ...prev,
            decision: {
              ...(prev.decision || {}),
              contribution_max: ref_contrib,
              frais_admin: frais,
              avec_contribution: ref_contrib > 0,
              gratuit: ref_contrib === 0,
            },
            service: {
              ...(prev.service || {}),
              contribution_max: ref_contrib,
              frais_admin: frais,
            },
          }));
        }
      } else if (res.data && res.data.self_reference) {
        setCheckState({ status: 'selfref', info: null });
      } else {
        setCheckState({ status: 'notfound', info: null });
      }
    } catch (e) {
      setCheckState({ status: 'error', info: null });
    }
  };

  // Reset quand la case est décochée
  useEffect(() => {
    if (!meme) setCheckState({ status: 'idle', info: null });
  }, [meme]);

  const feedback = (() => {
    if (!meme || !ref) return null;
    if (checkState.status === 'checking') return <span style={{ color: '#555', fontSize: 11 }}>⏳ Vérification…</span>;
    if (checkState.status === 'ok') {
      const i = checkState.info || {};
      const who = [i.prenom, i.nom].filter(Boolean).join(' ') || '(sans nom)';
      return <span style={{ color: '#0a7d0a', fontSize: 11, fontWeight: 600 }} data-testid="meme-affaire-ok">✓ {who}{i.nature ? ` — ${i.nature}` : ''}</span>;
    }
    if (checkState.status === 'short') return <span style={{ color: '#666', fontSize: 11, fontStyle: 'italic' }} data-testid="meme-affaire-short">ℹ Saisir les 8 premiers caractères (exclure le no image — legacy MDIParent2.vb:4301)</span>;
    if (checkState.status === 'selfref') return <span style={{ color: '#c02020', fontSize: 11, fontWeight: 600 }} data-testid="meme-affaire-selfref">✗ Le champ fait référence à lui-même (legacy MDIParent2.vb:4296)</span>;
    if (checkState.status === 'notfound') return <span style={{ color: '#c07a00', fontSize: 11, fontWeight: 500 }} data-testid="meme-affaire-notfound" title="Aucun dossier avec ce numéro trouvé — vérifiez ou continuez si vous êtes sûr">⚠ Dossier introuvable dans les Attestations (info seulement — l&apos;enregistrement reste possible)</span>;
    if (checkState.status === 'error') return <span style={{ color: '#c07a00', fontSize: 11 }}>⚠ Erreur de vérification</span>;
    return null;
  })();

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
        <WinCheckbox
          label="Même affaire que :"
          checked={meme}
          onChange={v => {
            if (!v) {
              // Décochage : parité legacy CodeInfoFin.vb:507 —
              // Reset des infos reportées + recalcul de ContMax() sur
              // base des données financières du dossier courant.
              // La logique est centralisée dans App.js (handleUncheckMemeAffaire)
              // pour avoir accès à toutes les données du formulaire (revenus,
              // actifs, catégorie, date_demande...).
              setCheckState({ status: 'idle', info: null });
              if (typeof handleUncheckMemeAffaire === 'function') {
                handleUncheckMemeAffaire();
              } else {
                // Fallback minimal si le callback n'est pas fourni
                updateService('meme_affaire', false);
                updateService('meme_affaire_ref', '');
              }
            } else {
              updateService('meme_affaire', true);
            }
          }}
          data-testid="chk-meme-affaire"
        />
        <input
          className="win-input"
          value={service.meme_affaire_ref || ''}
          onChange={e => updateService('meme_affaire_ref', e.target.value.toUpperCase())}
          onBlur={e => validateRef(e.target.value)}
          maxLength={8}
          disabled={!meme}
          placeholder="8 chars"
          style={{ textTransform: 'uppercase', width: 110, flex: '0 0 auto' }}
          data-testid="input-meme-affaire"
        />
      </div>
      {feedback && (
        <div style={{ marginTop: 2, paddingLeft: 24, minHeight: 14 }} data-testid="meme-affaire-feedback">
          {feedback}
        </div>
      )}
      {meme && (
        <div style={{ marginTop: 2, paddingLeft: 24, fontSize: 10, color: '#666', fontStyle: 'italic' }}>
          ℹ Contribution max reprise du dossier référencé (adda560) — legacy CodeDaj.vb:2603.
        </div>
      )}
    </>
  );
};

const ServiceTab = ({ formData, updateService, setFormData, handleUncheckMemeAffaire }) => {
  const [categories, setCategories] = useState([]);   // NatCateg.description[]
  const [natures, setNatures] = useState([]);         // Natures filtré par catégorie
  const [services, setServices] = useState([]);       // NatServ filtré par bitmap
  const [techniciens, setTechniciens] = useState([]); // avocats (code LIKE 'T%' AND Actif=1)

  // Validation « No dossier de cour » (adda544) — mode SOUPLE (principal) :
  // indicateur rouge sans vider le champ. Case « sans masque » = échappatoire Legacy.
  const [courNoErr, setCourNoErr] = useState("");
  const [courSansMasque, setCourSansMasque] = useState(false);
  const handleCourNoBlur = (val) => {
    if (courSansMasque) { setCourNoErr(""); return; }
    const res = validateCourNo(val);
    if (res.ok) {
      setCourNoErr("");
      const c = cleanCourNo(val);
      if (c.length === 14) updateService('no_dossier_cour', formatCourNo(val));
    } else {
      setCourNoErr(courNoMessage(res.reason));
    }
  };

  // Verrou : dès que l'utilisateur édite MANUELLEMENT le champ CODE, on désactive
  // les effets d'auto-recovery (2-bis/3-bis) qui re-dérivent le code depuis les
  // descriptions encore affichées — sinon taper la 1re lettre re-remplit le champ
  // avec l'ancien code. Réinitialisé au changement de dossier.
  const manualCodeEditRef = useRef(false);
  useEffect(() => { manualCodeEditRef.current = false; }, [formData.no_sequence]);

  const service = formData.service || {};
  const decision = formData.decision || {};
  // Trim défensif — si la donnée vient du legacy CHAR(N) paddé.
  const categorie = (service.categorie || '').trim();
  const natureDesc = (service.nature || '').trim();
  const serviceDesc = (service.service_nom || '').trim();
  const codeNature5 = (service.code_nature || '').trim().substring(0, 5);
  const codeService1 = (service.code_nature || '').trim().substring(5, 6);

  // 0️⃣ Auto-hydratation depuis le CODE quand les descriptions sont absentes
  // (cas legacy : dossier stocke uniquement adda510 sans adda500/501/502).
  // 2️⃣ AUTO-EXPLODE code → catégorie/nature/service
  // Équivalent legacy `ExploseNature` — CodeDaj.vb:4577.
  //
  // Comportement legacy VB.NET : dès que l'utilisateur tape/colle un code
  // complet (5 chars nature seule OU 6 chars nature+service), l'app fait
  // exploser le code et remplit systématiquement les 3 champs dérivés
  // (categorie, nature, service_nom). C'est un comportement d'override
  // volontaire — la source de vérité est le code, pas les dropdowns.
  //
  // Bug 2026-02-17 : l'ancien guard bloquait l'explode si l'un des 3 champs
  // était déjà rempli (chargement de dossier legacy avec categorie set) →
  // taper "H0046C" ne mettait plus le service dropdown à jour.
  useEffect(() => {
    const rawCode = (service.code_nature || '').trim().toUpperCase();
    // Seul un code complet (5 ou 6 chars) déclenche l'explode
    if (rawCode.length !== 5 && rawCode.length !== 6) return;

    axios.get(`${API}/reference/natures-by-code/${encodeURIComponent(rawCode)}`)
      .then(res => {
        const d = res.data || {};
        const nat = d.nature || {};
        const srv = d.service || {};
        setFormData(prev => ({
          ...prev,
          service: {
            ...prev.service,
            categorie: (nat.categorie || '').trim(),
            nature: (nat.description || '').trim(),
            // Pour code 5-chars sans service, srv est null → service_nom = ""
            service_nom: (srv.description || '').trim(),
            code_nature: d.code_6 || prev.service?.code_nature,
          },
        }));
      })
      .catch(e => {
        console.warn(`[ServiceTab] Code ${rawCode} introuvable dans le référentiel:`, e?.response?.status);
      });
  }, [service.code_nature]);

  // 1️⃣ Chargement initial : catégories (NatCateg) + techniciens
  useEffect(() => {
    axios.get(`${API}/reference/nat-categ`)
      .then(res => setCategories((res.data || []).map(r => r.description)))
      .catch(e => console.error('Erreur chargement NatCateg', e));
    // Techniciens/Témoins actifs (avocats.code LIKE 'T%' AND Actif=1)
    axios.get(`${API}/reference/techniciens`)
      .then(res => setTechniciens(res.data || []))
      .catch(e => console.error('Erreur chargement techniciens', e));
  }, []);

  // Sélection du technicien : met à jour code_tech ET nom_tech simultanément
  // (mapping legacy daj.CodeTech + daj.NomTech, cf. avocats.vb).
  const handleTechnicienChange = (code) => {
    const tech = techniciens.find(t => t.code === code);
    setFormData(prev => ({
      ...prev,
      service: {
        ...prev.service,
        code_tech: code,
        nom_tech: tech ? tech.label : '',
      },
    }));
  };

  // 2️⃣ Cascade Catégorie → Natures
  useEffect(() => {
    if (!categorie) { setNatures([]); return; }
    axios.get(`${API}/reference/natures`, { params: { categorie } })
      .then(res => setNatures(res.data || []))
      .catch(e => console.error('Erreur chargement Natures', e));
  }, [categorie]);

  // 2-bis : Auto-recovery du code_nature pour les vieux dossiers legacy.
  useEffect(() => {
    if (manualCodeEditRef.current) return;
    if (!natureDesc || codeNature5 || natures.length === 0) return;
    const match = natures.find(n => (n.description || '').trim() === natureDesc);
    if (match) {
      setFormData(prev => ({
        ...prev,
        service: { ...prev.service, code_nature: match.code },
      }));
    }
  }, [natureDesc, codeNature5, natures, setFormData]);

  // 3️⃣ Cascade Nature → Services (bitmap filter côté backend)
  useEffect(() => {
    if (!codeNature5) { setServices([]); return; }
    axios.get(`${API}/reference/nat-serv`, { params: { nature_code: codeNature5 } })
      .then(res => setServices(res.data || []))
      .catch(e => console.error('Erreur chargement NatServ', e));
  }, [codeNature5]);

  // 3-bis : Auto-recovery du code service (6e char) si on a la description
  useEffect(() => {
    if (manualCodeEditRef.current) return;
    // N'agit que si le code nature est COMPLET (5 chars) — évite d'appendre un
    // code service pendant que l'utilisateur tape un nouveau code.
    if (codeNature5.length !== 5) return;
    if (!serviceDesc || codeService1 || services.length === 0 || !codeNature5) return;
    const match = services.find(s => (s.description || '').trim() === serviceDesc);
    if (match) {
      setFormData(prev => ({
        ...prev,
        service: { ...prev.service, code_nature: codeNature5 + match.code },
      }));
    }
  }, [serviceDesc, codeService1, services, codeNature5, setFormData]);

  const handleCategorieChange = (cat) => {
    setFormData(prev => ({
      ...prev,
      service: {
        ...prev.service,
        categorie: cat, nature: '', code_nature: '', service_nom: '',
      },
    }));
  };

  // Auto-checks legacy pour codes spéciaux (parité MDIParent2.vb:1976-1988).
  //   A0015 → RadEnDemande.Checked = True
  //   A0016 → RadEnDefense.Checked = True
  // Retourne { en_demande: 1|0 } partiel selon match, ou {} si aucun.
  const _autoEnDemandeFromCode = (code) => {
    const c = (code || '').toUpperCase();
    if (c.includes('A0015')) return { en_demande: 1 };
    if (c.includes('A0016')) return { en_demande: 0 };
    return {};
  };

  const handleNatureChange = (description) => {
    const nature = natures.find(n => n.description === description);
    const newCode = nature ? nature.code : '';
    const autoEnDem = _autoEnDemandeFromCode(newCode);
    setFormData(prev => ({
      ...prev,
      service: {
        ...prev.service,
        nature: description,
        code_nature: newCode,
        service_nom: '',
        ...autoEnDem,
      },
    }));
  };

  const handleServiceChange = (serviceDesc) => {
    const srv = services.find(s => s.description === serviceDesc);
    const fullCode = codeNature5 + (srv ? srv.code : '');
    const autoEnDem = _autoEnDemandeFromCode(fullCode);
    setFormData(prev => ({
      ...prev,
      service: {
        ...prev.service,
        service_nom: serviceDesc,
        code_nature: fullCode,
        ...autoEnDem,
      },
    }));
  };

  // Auto-check EnDemande/EnDéfense pour dossiers legacy chargés depuis DB
  // (parité MDIParent2.vb:1976-1988) — s'applique une seule fois par code
  // pour ne pas écraser un choix manuel de l'utilisateur.
  const _lastAutoCodeRef = useRef('');
  useEffect(() => {
    const code = (service.code_nature || '').toUpperCase();
    if (!code || code === _lastAutoCodeRef.current) return;
    const autoEnDem = _autoEnDemandeFromCode(code);
    if (Object.keys(autoEnDem).length === 0) return;
    // Applique seulement si en_demande n'est pas déjà défini à la valeur cible
    // (sinon on cause une boucle infinie / on écrase un choix manuel).
    if (service.en_demande === autoEnDem.en_demande) {
      _lastAutoCodeRef.current = code;
      return;
    }
    _lastAutoCodeRef.current = code;
    setFormData(prev => ({
      ...prev,
      service: { ...prev.service, ...autoEnDem },
    }));
  }, [service.code_nature, service.en_demande, setFormData]);

  // Contribution max vient de la décision (lecture seule, calculée par
  // BaremeCalcul) — fallback vers service.* pour rétrocompat.
  const contribMaxAffiche = service.contribution_max ?? decision.contribution_max ?? 0;

  return (
    <div className="win-tab-content compact" data-testid="service-tab">

      {/* ===================== Disposition groupboxes (parité onglet Requérant) ===================== */}
      {(() => {
        const row = { display: 'grid', gridTemplateColumns: '150px 1fr', gap: 8, alignItems: 'center', marginBottom: 5 };
        return (
      <div>
        {/* Zone haute : Nature du service | Demande et montants */}
        <div className="win-two-columns" style={{ gridTemplateColumns: '1.3fr 1fr 0.55fr', alignItems: 'stretch' }}>

          {/* -------- NATURE DU SERVICE -------- */}
          <fieldset className="win-groupbox compact">
            <legend>Nature du service</legend>
            <div style={{ ...row, gridTemplateColumns: '150px 140px' }}>
              <label className="win-field-label">Code</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="win-input"
                  value={service.code_nature || ''}
                  onChange={e => {
                    const v = e.target.value;
                    manualCodeEditRef.current = true;
                    const autoEnDem = _autoEnDemandeFromCode(v);
                    // Nettoie les 3 dropdowns pendant l'édition du CODE ; l'auto-explode
                    // les repeuplera dès que le code sera complet (5/6 car.).
                    setFormData(prev => ({
                      ...prev,
                      service: {
                        ...prev.service,
                        code_nature: v,
                        categorie: '',
                        nature: '',
                        service_nom: '',
                        ...autoEnDem,
                      },
                    }));
                  }}
                  maxLength={6}
                  style={{ textAlign: 'center', fontWeight: 600 }}
                  data-testid="input-code-nature"
                />
                {(() => {
                  const cn = (service.code_nature || '').toUpperCase();
                  if (!cn) return null;
                  if (cn.startsWith('H')) {
                    return (
                      <span data-testid="badge-homolog"
                            title="Code Homologation — refus, transfert GCR et attestation soumis à des règles spécifiques (legacy MDIParent2.vb:1508)"
                            style={{
                              position: 'absolute', top: '50%', right: -8, transform: 'translate(100%, -50%)',
                              background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b',
                              padding: '1px 6px', borderRadius: 9, fontSize: 10, fontWeight: 700,
                              whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            }}>H · HOMOLOGATION</span>
                    );
                  }
                  if (cn.startsWith('I0064')) {
                    return (
                      <span data-testid="badge-sarpa"
                            title="Code SARPA — transfert GCR bloqué, PDFs spécifiques AttestSarpa/RefusSarpa (legacy MDIParent2.vb:1611)"
                            style={{
                              position: 'absolute', top: '50%', right: -8, transform: 'translate(100%, -50%)',
                              background: '#dcfce7', color: '#166534', border: '1px solid #22c55e',
                              padding: '1px 6px', borderRadius: 9, fontSize: 10, fontWeight: 700,
                              whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            }}>I0064 · SARPA</span>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            <div style={row}>
              <label className="win-field-label">Catégorie</label>
              <select
                className="win-select"
                value={categorie}
                onChange={e => handleCategorieChange(e.target.value)}
                data-testid="select-categorie"
              >
                <option value="">— Sélectionner —</option>
                {categorie && !categories.includes(categorie) && (
                  <option value={categorie} data-legacy="true">{categorie} (legacy)</option>
                )}
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={row}>
              <label className="win-field-label">Nature</label>
              <select
                className="win-select"
                value={natureDesc}
                onChange={e => handleNatureChange(e.target.value)}
                disabled={!categorie && !natureDesc}
                data-testid="select-nature"
              >
                <option value="">— Sélectionner —</option>
                {natureDesc && !natures.find(n => (n.description || '').trim() === natureDesc) && (
                  <option value={natureDesc} data-legacy="true">{natureDesc} (legacy)</option>
                )}
                {natures.map(n => (
                  <option key={n.code} value={n.description}>{n.description}</option>
                ))}
              </select>
            </div>

            <div style={row}>
              <label className="win-field-label">Service</label>
              <select
                className="win-select"
                value={serviceDesc}
                onChange={e => handleServiceChange(e.target.value)}
                disabled={!codeNature5 || (services.length === 0 && !serviceDesc)}
                data-testid="select-service"
              >
                <option value="">— Sélectionner —</option>
                {serviceDesc && !services.find(s => (s.description || '').trim() === serviceDesc) && (
                  <option value={serviceDesc} data-legacy="true">{serviceDesc} (legacy)</option>
                )}
                {services.map(s => (
                  <option key={s.code} value={s.description}>{s.description}</option>
                ))}
              </select>
            </div>

            <div style={row}>
              <label className="win-field-label"># Dossier de cour</label>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="win-input"
                    value={courSansMasque ? (service.no_dossier_cour || '') : maskCourNo(service.no_dossier_cour || '')}
                    onChange={e => { setCourNoErr(''); updateService('no_dossier_cour', courSansMasque ? e.target.value : maskCourNo(e.target.value)); }}
                    onBlur={e => handleCourNoBlur(e.target.value)}
                    placeholder="000-00-000000-000"
                    maxLength={17}
                    style={courNoErr ? { borderColor: '#cc0000' } : undefined}
                    data-testid="input-no-dossier-cour"
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={courSansMasque}
                      onChange={e => { setCourSansMasque(e.target.checked); if (e.target.checked) setCourNoErr(''); }}
                      data-testid="chk-cour-sans-masque"
                    />
                    Sans masque
                  </label>
                </div>
                {courNoErr && (
                  <div style={{ color: '#cc0000', fontSize: 12, marginTop: 3 }} data-testid="cour-no-error">
                    {courNoErr}
                  </div>
                )}
              </div>
            </div>

            <div style={{ ...row, gridTemplateColumns: '150px 140px' }}>
              <label className="win-field-label">Article</label>
              <input
                className="win-input"
                value={service.article || ''}
                onChange={e => updateService('article', e.target.value)}
                maxLength={20}
                data-testid="input-article"
              />
            </div>

            <div style={row}>
              <label className="win-field-label">Nature accessoire</label>
              <input
                className="win-input"
                value={service.nature_accessoire || ''}
                onChange={e => updateService('nature_accessoire', e.target.value)}
                maxLength={100}
                data-testid="input-nature-accessoire"
              />
            </div>

            <div style={row}>
              <label className="win-field-label">Fondement du droit</label>
              <input
                className="win-input"
                value={service.fondement_droit || ''}
                onChange={e => updateService('fondement_droit', e.target.value)}
                maxLength={100}
                data-testid="input-fondement-droit"
              />
            </div>
          </fieldset>

          {/* -------- DEMANDE ET MONTANTS -------- */}
          <fieldset className="win-groupbox compact">
            <legend>Demande et montants</legend>
            {/* En demande / En défense (exclusifs, vide possible) + Date signature */}
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 10, flexWrap: 'wrap' }}>
              <label className="win-checkbox" style={{ fontSize: 15, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={service.en_demande === 1}
                  onChange={e => updateService('en_demande', e.target.checked ? 1 : 2)}
                  data-testid="chk-en-demande"
                />
                En demande
              </label>
              <label className="win-checkbox" style={{ fontSize: 15, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={service.en_demande === 0}
                  onChange={e => updateService('en_demande', e.target.checked ? 0 : 2)}
                  data-testid="chk-en-defense"
                />
                En défense
              </label>
              <div className="win-field" style={{ marginLeft: 'auto' }}>
                <label className="win-field-label">Date de signature du requérant</label>
                <div style={{ width: 130 }}>
                  <DateInput
                    value={service.date_signature_req || ''}
                    onChange={v => updateService('date_signature_req', v)}
                    minDate={formData.date_demande || undefined}
                    minLabel="date de la demande"
                    testId="input-date-signature-req"
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <label className="win-field-label">Type de dossier :</label>
              <WinCheckbox
                label="Sommaire"
                checked={service.type_dossier_sommaire || false}
                onChange={v => updateService('type_dossier_sommaire', v)}
                data-testid="chk-sommaire"
              />
            </div>

            <MemeAffaireRow
              service={service}
              updateService={updateService}
              setFormData={setFormData}
              noclient={formData.noclient}
              region={formData.region}
              bureau={formData.bureau}
              currentNoref={formData.no_reference}
              handleUncheckMemeAffaire={handleUncheckMemeAffaire}
            />

            <div style={{ marginTop: 5 }}>
              <label className="win-field-label">Contribution maximale incluant les frais d'administration de 50&nbsp;$</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 3, alignItems: 'center' }}>
                <div style={{ position: 'relative', width: 130 }}>
                  <MoneyInput value={contribMaxAffiche} readOnly testId="input-contribution-max" />
                  {service.meme_affaire && service.meme_affaire_ref && (
                    <span
                      data-testid="badge-repris-de"
                      title={`Contribution max reprise du dossier ${service.meme_affaire_ref} (legacy CodeDaj.vb:2603)`}
                      style={{
                        position: 'absolute', top: '50%', right: -8, transform: 'translate(100%, -50%)',
                        background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd',
                        padding: '1px 6px', borderRadius: 9, fontSize: 10, fontWeight: 600,
                        whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      }}
                    >🔗 Repris de {service.meme_affaire_ref}</span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 5 }}>
              <label className="win-field-label">Coût réel prévisible de services</label>
              <div style={{ width: 130, marginTop: 3 }}>
                <MoneyInput
                  value={service.cout_reel || 0}
                  onChange={v => updateService('cout_reel', v)}
                  testId="input-cout-reel"
                />
              </div>
            </div>

            <div style={{ marginTop: 5 }}>
              <label className="win-field-label">Montant déjà versé</label>
              <div style={{ width: 130, marginTop: 3 }}>
                <MoneyInput
                  value={service.montant_deja_verse || 0}
                  onChange={v => updateService('montant_deja_verse', v)}
                  testId="input-montant-verse"
                />
              </div>
            </div>
          </fieldset>

          {/* -------- GCR RECOUVREMENT (3e colonne) -------- */}
          <fieldset className="win-groupbox compact" data-testid="gcr-group">
            <legend>GCR Recouvrement</legend>
            <WinCheckbox
              label="Activer le recouvrement"
              checked={service.gcr_recouv || false}
              onChange={v => updateService('gcr_recouv', v)}
              data-testid="chk-gcr-recouv"
            />
            <div style={{ ...row, gridTemplateColumns: '70px 130px', marginTop: 8 }}>
              <label className="win-field-label">Date</label>
              <DateInput
                value={service.gcr_date || ''}
                onChange={v => updateService('gcr_date', v)}
                disabled={!service.gcr_recouv}
                testId="input-gcr-date"
              />
            </div>
            <div style={{ ...row, gridTemplateColumns: '70px 130px' }}>
              <label className="win-field-label">Montant</label>
              <MoneyInput
                value={service.gcr_montant || 0}
                onChange={v => updateService('gcr_montant', v)}
                disabled={!service.gcr_recouv}
                testId="input-gcr-montant"
              />
            </div>
          </fieldset>
        </div>

        {/* Zone basse : Témoin + Notes (pleine largeur) */}
        <fieldset className="win-groupbox compact">
          <legend>Témoin et notes</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' }}>
            <div className="win-field">
              <label className="win-field-label">Témoin</label>
              <select
                className="win-select"
                value={(service.code_tech || '').trim()}
                onChange={e => handleTechnicienChange(e.target.value)}
                data-testid="select-temoin"
              >
                <option value="">— Sélectionner —</option>
                {service.code_tech
                  && !techniciens.find(t => t.code === service.code_tech) && (
                  <option value={service.code_tech} data-legacy="true">
                    {service.code_tech} — {service.nom_tech || '(inactif)'} (legacy)
                  </option>
                )}
                {techniciens.map(t => (
                  <option key={t.code} value={t.code}>
                    {t.code} — {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="win-field">
              <label className="win-field-label">Notes</label>
              <textarea
                className="win-input"
                value={service.notes_service || ''}
                onChange={e => updateService('notes_service', e.target.value)}
                maxLength={200}
                rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 'inherit', width: '100%' }}
                data-testid="input-notes-service"
              />
            </div>
          </div>
        </fieldset>
      </div>
        );
      })()}
    </div>
  );
};

export default ServiceTab;
